import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiUser } from 'react-icons/fi';
import { Screen } from '../../components/ui/Screen';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { createUserWithEmail, waitForAuthReady } from '../../services/firebase/auth.service';
import { saveUserProfile } from '../../services/userProfile.service';
import { useAppContext } from '../../store/AppContext';

export function RegisterScreen() {
  const navigate = useNavigate();
  const { user, loading: userLoading, onboardingComplete, profileSettled, authError } = useAppContext();
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Already authed: route based on onboarding state once profile is settled
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (!userLoading && user) {
      if (profileSettled) {
        navigate(onboardingComplete ? '/home' : '/onboarding', { replace: true });
      }
    }
  }, [user, userLoading, profileSettled, onboardingComplete, navigate]);

  useEffect(() => {
    if (authError && !userLoading) {
      setError(authError.message);
    }
  }, [authError, userLoading]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError('Please enter both email and password.');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      setLoading(false);
      return;
    }

    try {
      const authUser = await createUserWithEmail(email, password);

      // Wait until Firebase auth is fully settled before any Firestore write.
      // Pass the expected UID so we resolve only for this specific user,
      // not any stale auth state. Single gate for all new user writes.
      await waitForAuthReady(authUser.uid);

      // Store firstName immediately so it's available throughout onboarding.
      // sessionStorage write is synchronous and survives the navigate — ensures
      // OnboardingScreen has the name even if AppContext profile load hasn't resolved yet.
      // cleanedFirstName is the single sanitized value used for both storage and profile save.
      const cleanedFirstName = firstName.trim();
      if (cleanedFirstName) {
        try { sessionStorage.setItem('onboard_firstName', cleanedFirstName); } catch { /* ignore */ }
        await saveUserProfile(authUser.uid, { firstName: cleanedFirstName });
      }

      navigate('/onboarding', { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create account. Please try again.';
      if (message.includes('auth/email-already-in-use')) {
        setError('This email is already registered. Please sign in instead.');
      } else if (message.includes('auth/invalid-email')) {
        setError('Please enter a valid email address.');
      } else if (message.includes('auth/weak-password')) {
        setError('Password is too weak. Please use a stronger password.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }

  if (userLoading) {
    return (
      <Screen title="Loading..." subtitle="Checking authentication...">
        <Card>
          <p className="text-center text-gray-500">Loading...</p>
        </Card>
      </Screen>
    );
  }

  if (user) {
    return null;
  }

  return (
    <Screen
      title="Create account"
      subtitle="A few quick details to get started."
    >
      <Card>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="First name (optional)"
            name="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="What should we call you?"
            leftIcon={<FiUser />}
            disabled={loading}
          />
          <Input
            label="Email"
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            leftIcon={<FiMail />}
            error={error && error.includes('email') ? error : undefined}
            required
            disabled={loading}
          />
          <Input
            label="Password"
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            leftIcon={<FiLock />}
            error={error && (error.includes('password') || error.includes('Password')) ? error : undefined}
            helperText="Must be at least 6 characters"
            required
            disabled={loading}
          />
          {error && !error.includes('email') && !error.includes('password') && !error.includes('Password') && (
            <p className="text-sm text-red-600">{error}</p>
          )}
          <Button fullWidth type="submit" disabled={loading}>
            {loading ? 'Creating account...' : 'Continue'}
          </Button>
          <p className="text-center text-xs text-gray-500">
            Already have an account?{' '}
            <Link to="/auth/login" className="font-medium text-gray-900">
              Log in
            </Link>
          </p>
        </form>
      </Card>
    </Screen>
  );
}
